import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import SrPartnerListingCard, {
  SR_PARTNER_LISTING_CARD_TAG_THEME_GREEN,
  SR_PARTNER_LISTING_CARD_TAG_THEME_PURPLE,
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
} from 'client/components/SrPartnerListingCard';
import SrPartnerList from 'client/components/SrPartnerList';
import SrIcon from 'client/components/SrIcon';
import branch from 'client/core/branch';

import {
  LISTING_IMAGE_PLACEHOLDER_URL,
  LISTING_SERVICE_TYPE_TENANT_SCREENING,
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_PENDING,
} from 'common/listingConstants';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
  DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_TENANT_SCREENING,
} from 'common/dataTableConstants';
import { getListingApplyURL } from 'common/listingHelpers';

import { dataTable, actionDataTableLoadNextPage } from 'client/helpers/dataTables';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';

export function getDataTableSettingsFromProps(props) {
  const marketIds =
    props.currentUser?.settings?.partnerPortal?.listingsAcceptingApplications?.[
      props.partner.slug + '_marketIds'
    ]; // eslint-disable-line max-len

  const viewId = `${DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_TENANT_SCREENING}_${props.partner.id}`;
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId,
    filter: {
      'listings.status': [LISTING_STATUS_ACTIVE, LISTING_STATUS_PENDING],
      'listings.partnerId': props.partner.id,
    },
    customParams: {
      partnerListings: {
        sinceDate: props.sinceDate,
        serviceType: LISTING_SERVICE_TYPE_TENANT_SCREENING,
      },
    },
    sort: {
      lastActiveDate: 'desc',
    },
    count: 40,
  };

  if (props.sinceDate) {
    dataTableSettings.viewId = `${dataTableSettings.viewId}_sinceDate:${props.sinceDate}`;
  }

  if (marketIds && marketIds.length > 0) {
    dataTableSettings.customParams.partnerListings.marketIds = marketIds;
    dataTableSettings.viewId = `${dataTableSettings.viewId}markets_${marketIds.join('_')}`;
  }

  return dataTableSettings;
}

@branch({
  currentUser: ['currentUser'],
  appUrl: ['env', 'APP_URL'],
})
@dataTable(getDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 80,
})
class SrDataTablePartnerTentantScreeningListings extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
    items: PropTypes.array.isRequired,
    apartment: PropTypes.object,
    apartmentBuildingId: PropTypes.array, // eslint-disable-line react/no-unused-prop-types
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    partnerListingsViewId: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
  };

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettingsFromProps(this.props));
    }
  };

  _getTitle = (listing) => {
    const { apartment } = this.props;
    const isUnit = !!apartment;
    let title = listing.addressStreetDisplay;

    if (isUnit) {
      const apartmentUnit = listing.apartmentUnit || '';
      title = apartmentUnit.toLowerCase().includes('unit') ? (
        apartmentUnit
      ) : (
        <span>
          <span>{`Unit ${apartmentUnit || '--'} `}</span>
          {listing.floorPlanName && (
            <span className="floorPlanName">{`(${listing.floorPlanName})`}</span>
          )}
        </span>
      );
    }
    return title;
  };

  _getTags = (listing) => {
    return lodashCompact([
      listing.appApproved && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_GREEN,
        label: 'lease pending',
      },
      listing.appInProgress && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_PURPLE,
        label: 'apps in progress',
      },
    ]);
  };

  _getStats = (listing) => {
    const appsCompleted = listing?.totalAppsInProgress || 0;
    const appsStarted = listing?.totalAppsStarted || 0;
    const prequalifications = listing?.totalPrequalifications || 0;

    return [
      `${appsCompleted} Completed`,
      `${appsStarted} Started`,
      `${prequalifications} Prequaled`,
    ];
  };

  _getCaption = (listing) => {
    const { apartment } = this.props;
    const isUnit = !!apartment;

    if (isUnit) {
      return [
        `${listing.apartmentName} |`,
        listing.addressStreetNumber,
        listing.addressStreetName,
      ].join(' ');
    }

    return '';
  };

  _getBottomChips = (listing) => {
    const label = [`${listing.addressCity},`, listing.addressStateCode].join(' ');

    return lodashCompact([
      {
        label,
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
      },
      listing.propertyManager && {
        label: (
          <>
            <SrIcon className="person-single-dark" marginRight={8} display="inlineBlock" />
            {listing.propertyManager.name}
          </>
        ),
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
      },
    ]);
  };

  render() {
    const {
      items: listings,
      onInfiniteScrollWrapperRef,
      dataTableApiState,
      partner,
      appUrl,
    } = this.props;

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <SrPartnerList singleRow>
          {listings.map((listing) => {
            const stats = this._getStats(listing);
            const title = this._getTitle(listing);
            const tags = this._getTags(listing);
            const applyUrl = getListingApplyURL(listing, appUrl);
            const caption = this._getCaption(listing);
            const bottomChips = this._getBottomChips(listing);

            return (
              <SrPartnerListingCard
                to={`/partner/${partner.slug}/listing/${listing.id}`}
                key={listing.id}
                photos={[LISTING_IMAGE_PLACEHOLDER_URL]}
                title={title}
                textToCopy={applyUrl}
                caption={caption}
                stats={stats}
                tags={tags}
                bottomChips={bottomChips}
              />
            );
          })}
        </SrPartnerList>

        {dataTableApiState.inProgress && <div className="loading-ellipses" />}
      </div>
    );
  }
}

SrDataTablePartnerTentantScreeningListings.displayName =
  'SrDataTablePartnerTentantScreeningListings';

export default SrDataTablePartnerTentantScreeningListings;
